import { makeAutoObservable } from "mobx";
import { v4 as uuidv4 } from "uuid";

export interface NetworkRequest {
    requestId: string;
    url: string;
    method: string;
    headers: Record<string, string>;
    postData: string | null;
    resourceType: string;
    timestamp: number;
    status: number | null;
    statusText: string;
    responseHeaders: Record<string, string>;
    mimeType: string;
    responseTimestamp: number | null;
    body: string | null;
    base64Encoded: boolean;
    truncated: boolean;
    failed: boolean;
    errorText: string | null;
    duration: number | null;
}

export interface ConsoleEntry {
    level: string;
    text: string;
    timestamp: number;
}

export interface BrowserSession {
    id: string;
    label: string;
    url: string;
    isStreaming: boolean;
    whepUrl: string | null;
    cssCursor: string;
    _pendingStream: boolean;
    networkRequests: NetworkRequest[];
    consoleEntries: ConsoleEntry[];
}

export class BrowserStore {
    browserSessions: BrowserSession[] = [];

    private _root: any;

    constructor(root: any) {
        this._root = root;
        makeAutoObservable(this, { _root: false });
    }

    syncSessions(sessions: Array<{ id: string; cwd: string | null; current_url: string | null; is_alive: boolean; is_streaming: boolean }>) {
        const serverIds = new Set(sessions.filter(s => s.is_alive).map(s => s.id));
        this.browserSessions = this.browserSessions.filter(s => serverIds.has(s.id));
        for (const s of sessions) {
            if (!s.is_alive) continue;
            const existing = this.browserSessions.find(b => b.id === s.id);
            // TODO(app-model): "start_browser_stream" no longer has a backend handler (old
            // agent.api.handlers.browser was removed) -- wire to BrowserSession.start_streaming
            // via the app-model RPC surface once this store holds a container entity.
            if (existing) {
                const wasStreaming = existing.isStreaming;
                existing.isStreaming = s.is_streaming;
            } else {
                const url = s.current_url ?? "";
                const label = url || (s.cwd ? s.cwd.split("/").pop() || "browser" : "browser");
                this.browserSessions.push({ id: s.id, label, url, isStreaming: s.is_streaming, whepUrl: null, cssCursor: "default", _pendingStream: false, networkRequests: [], consoleEntries: [] });
            }
        }
    }

    syncSession(d: { browser_session_id: string; is_alive: boolean; is_streaming: boolean; whep_url?: string | null }) {
        const session = this.browserSessions.find(s => s.id === d.browser_session_id);
        if (!session) return;
        if (!d.is_alive) {
            this.browserSessions = this.browserSessions.filter(s => s.id !== d.browser_session_id);
            return;
        }
        // TODO(app-model): "browser_navigate" / "start_browser_stream" no longer have backend
        // handlers (old agent.api.handlers.browser was removed) -- wire to
        // BrowserSession.navigate / start_streaming via the app-model RPC surface once this
        // store holds a container entity.
        if (session._pendingStream) {
            session._pendingStream = false;
        }
        session.isStreaming = d.is_streaming;
        if (d.whep_url !== undefined) session.whepUrl = d.whep_url ?? null;
    }

    openBrowserSession(url: string): string {
        const id = uuidv4();
        const label = url || "browser";
        this.browserSessions.push({ id, label, url, isStreaming: false, whepUrl: null, cssCursor: "default", _pendingStream: true, networkRequests: [], consoleEntries: [] });
        // TODO(app-model): "create_browser_session" no longer has a backend handler (old
        // agent.api.handlers.browser was removed) -- DeploymentContext.create_browser_session
        // already exists on the app model; wire this once this store holds a container entity.
        return id;
    }

    setCursor(d: { browser_session_id: string; css_cursor: string }) {
        const session = this.browserSessions.find(s => s.id === d.browser_session_id);
        if (session) session.cssCursor = d.css_cursor;
    }

    addNetworkRequest(d: { browser_session_id: string; request_id: string; url: string; method: string; headers: Record<string, string>; post_data: string | null; resource_type: string; timestamp: number }) {
        const session = this.browserSessions.find(s => s.id === d.browser_session_id);
        if (!session) return;
        const req: NetworkRequest = {
            requestId: d.request_id,
            url: d.url,
            method: d.method,
            headers: d.headers,
            postData: d.post_data,
            resourceType: d.resource_type,
            timestamp: d.timestamp,
            status: null,
            statusText: "",
            responseHeaders: {},
            mimeType: "",
            responseTimestamp: null,
            body: null,
            base64Encoded: false,
            truncated: false,
            failed: false,
            errorText: null,
            duration: null,
        };
        session.networkRequests.push(req);
        if (session.networkRequests.length > 500) session.networkRequests.splice(0, session.networkRequests.length - 500);
    }

    addNetworkResponse(d: { browser_session_id: string; request_id: string; status: number; status_text: string; headers: Record<string, string>; mime_type: string; timestamp: number }) {
        const session = this.browserSessions.find(s => s.id === d.browser_session_id);
        if (!session) return;
        const req = session.networkRequests.find(r => r.requestId === d.request_id);
        if (!req) return;
        req.status = d.status;
        req.statusText = d.status_text;
        req.responseHeaders = d.headers;
        req.mimeType = d.mime_type;
        req.responseTimestamp = d.timestamp;
        req.duration = req.responseTimestamp - req.timestamp;
    }

    addNetworkBody(d: { browser_session_id: string; request_id: string; body: string; base64_encoded: boolean; truncated: boolean }) {
        const session = this.browserSessions.find(s => s.id === d.browser_session_id);
        if (!session) return;
        const req = session.networkRequests.find(r => r.requestId === d.request_id);
        if (!req) return;
        req.body = d.body;
        req.base64Encoded = d.base64_encoded;
        req.truncated = d.truncated;
    }

    addNetworkFailed(d: { browser_session_id: string; request_id: string; error_text: string; timestamp: number }) {
        const session = this.browserSessions.find(s => s.id === d.browser_session_id);
        if (!session) return;
        const req = session.networkRequests.find(r => r.requestId === d.request_id);
        if (!req) return;
        req.failed = true;
        req.errorText = d.error_text;
        req.responseTimestamp = d.timestamp;
        req.duration = req.responseTimestamp - req.timestamp;
    }

    addConsoleEntry(d: { browser_session_id: string; level: string; text: string; timestamp: number }) {
        const session = this.browserSessions.find(s => s.id === d.browser_session_id);
        if (!session) return;
        session.consoleEntries.push({ level: d.level, text: d.text, timestamp: d.timestamp });
        if (session.consoleEntries.length > 500) session.consoleEntries.splice(0, session.consoleEntries.length - 500);
    }

    addConsoleResult(d: { browser_session_id: string; expression: string; result: string | null; error: string | null }) {
        const session = this.browserSessions.find(s => s.id === d.browser_session_id);
        if (!session) return;
        session.consoleEntries.push({ level: "info", text: `> ${d.expression}`, timestamp: Date.now() / 1000 });
        if (d.error != null) {
            session.consoleEntries.push({ level: "error", text: d.error, timestamp: Date.now() / 1000 });
        } else if (d.result != null) {
            session.consoleEntries.push({ level: "log", text: d.result, timestamp: Date.now() / 1000 });
        }
        if (session.consoleEntries.length > 500) session.consoleEntries.splice(0, session.consoleEntries.length - 500);
    }

    clearNetworkRequests(id: string) {
        const session = this.browserSessions.find(s => s.id === id);
        if (session) session.networkRequests.splice(0);
    }

    clearConsoleEntries(id: string) {
        const session = this.browserSessions.find(s => s.id === id);
        if (session) session.consoleEntries.splice(0);
    }

    resetStreamUrl(id: string) {
        const session = this.browserSessions.find(s => s.id === id);
        if (session) session.whepUrl = null;
    }

    removeBrowserSession(id: string) {
        this.browserSessions = this.browserSessions.filter(s => s.id !== id);
        // TODO(app-model): "destroy_browser_session" no longer has a backend handler (old
        // agent.api.handlers.browser was removed) -- DeploymentContext.destroy_browser_session
        // already exists on the app model; wire this once this store holds a container entity.
    }
}
